import React, { ReactNode, useEffect, useState } from "react";

import { Box, CircularProgress } from "@mui/material";

import { theme } from "@/src/theme/theme";

import { UnifiedCarouselImage } from "@/src/types/carousel";

interface DefaultImageRendererProps {
  image: UnifiedCarouselImage;
  index: number;
  height?: string | number;
}

export const DefaultImageRenderer = ({
  image,
  index,
  height
}: DefaultImageRendererProps): ReactNode => {
  const [isLoading, setIsLoading] = useState(true);
  const [isBroken, setIsBroken] = useState(false);
  const [useFallbackUrl, setUseFallbackUrl] = useState(false);

  // Use url (sized image) by default, fallback to lightboxUrl (high-res) if available
  const imageSrc = useFallbackUrl
    ? (image.lightboxUrl ?? image.url)
    : image.url;

  const handleImageError = () => {
    // If url failed and we haven't tried fallback yet, try lightboxUrl
    if (
      image.lightboxUrl &&
      !useFallbackUrl &&
      image.lightboxUrl !== image.url
    ) {
      setUseFallbackUrl(true);
      setIsLoading(true);
    } else {
      setIsLoading(false);
      setIsBroken(true);
    }
  };

  const handleImageLoad = () => {
    setIsLoading(false);
    setIsBroken(false);
  };

  // Reset all per-image state when image changes
  useEffect(() => {
    setIsLoading(true);
    setIsBroken(false);
    setUseFallbackUrl(false);
  }, [image.id]);

  return (
    <Box
      sx={{
        width: "100%",
        height: height ?? "100%",
        minHeight: height ? undefined : "200px",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.palette.common.black,
        overflow: "hidden"
      }}
    >
      {image.url && (
        <Box
          sx={{
            position: "absolute",
            top: -20,
            left: -20,
            right: -20,
            bottom: -20,
            backgroundImage: `url(${image.url})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "blur(20px) brightness(0.4)",
            zIndex: 1,
            opacity: 0.8
          }}
        />
      )}

      {isLoading && !isBroken && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 5
          }}
        >
          <CircularProgress
            size={48}
            thickness={3.5}
            sx={{ color: theme.palette.semantic.info.main }}
          />
        </Box>
      )}

      {!isBroken && (
        <Box
          component="img"
          src={imageSrc}
          alt={image.alt ?? `Image ${index + 1}`}
          onLoad={handleImageLoad}
          onError={handleImageError}
          sx={{
            maxWidth: "100%",
            maxHeight: "100%",
            width: "auto",
            height: "auto",
            objectFit: "contain",
            opacity: isLoading ? 0 : 1,
            transition: "opacity 0.2s ease-in-out",
            position: "relative",
            zIndex: 2
          }}
        />
      )}

      {isBroken && (
        <Box
          component="img"
          src="/assets/images/missing_car.jpg"
          alt="Missing image"
          sx={{
            maxWidth: "100%",
            maxHeight: "100%",
            width: "auto",
            height: "auto",
            objectFit: "contain",
            position: "relative",
            zIndex: 2
          }}
        />
      )}
    </Box>
  );
};
